import { CardDef, DeckDef, AbilityType, Rarity, getAbilityIcon } from '../types.js';
import type { Target } from '../shared/targeting.js';
import { PlayerData, getDeckCardCount, addCardToDeck, removeCardFromDeck } from '../collection/PlayerState.js';
import { getConfig } from '../config.js';
import { createCardElement, hideTooltip } from './CardView.js';
import { isCustomCardId, createShareButton } from './Share.js';
import { getAbilityDescription, getActionDescription } from '../engine/EffectText.js';
import { requestImage, type ImageRequest } from './ImageLoader.js';
import { showPrompt } from './NoticeView.js';
import {
  RARITY_ORDER, getCardPower, getCardSpirit, getCardClock,
  getCardEffect, getCardAbilityType, getCardTarget,
  ABILITY_TYPE_ORDER, ABILITY_TYPE_LABEL, TARGET_ORDER, TARGET_LABEL,
  effectLabel,
} from './CardSort.js';
import { positionDropdown } from './DropdownPosition.js';

const DRAG_MIME = 'text/x-cardverse-card';
const DRAG_MIME_REMOVE = 'text/x-cardverse-card-remove';

// Hard cap on the working deck size in the editor. Lets the player overshoot
// the required deck size temporarily so they can add-then-remove instead of
// being forced to remove first. Save remains gated on hitting `minDeckSize`
// exactly.
const DECK_OVER_CAP = 60;

// Hover-delay before showing a tile tooltip. Without this, sweeping the
// cursor across the grid flashes tooltips on every card the pointer crosses,
// which reads as lag.
const TOOLTIP_HOVER_DELAY_MS = 800;

type SortKey = 'rarity' | 'power' | 'spirit' | 'clock' | 'intensity' | 'type' | 'name' | 'acquired';

/** Effects whose intensity is denominated in power/HP/damage/armor units
 *  (hundreds), as opposed to count units (1, 2, 3...) for things like
 *  draw or steal-spirit. Mirrors the lists in cardgen/pipeline.ts:
 *  POWER_VALUED_VERBS + POWER_VALUED_ATOMIC_EFFECTS. Used by the
 *  intensity sort so a `damage 400` card doesn't strictly outrank a
 *  `draw 2` card; we normalize the power-valued effects by ÷100. */
const POWER_VALUED_EFFECTS = new Set([
  // Verbs (require a target on the card)
  'damage', 'boost', 'armor', 'slay',
  // Atomic effects
  'damage-opponent', 'heal', 'equalize', 'sacrifice-heal',
  'passive-boost-allies', 'passive-intimidate', 'passive-support-bonus',
  'passive-damage-exhausted', 'passive-rebirth',
]);

/** Sort-key intensity for a card. Returns a normalized "intensity unit"
 *  — power-valued effects divided by 100 so a 300-damage card and a
 *  3-draw card both score as 3. Cards without an intensity (no ability,
 *  no-intensity verbs like `exhaust`, etc.) score 0 so they sort to the
 *  bottom of the descending intensity order. */
function getCardIntensity(def: CardDef): number {
  let effect: string | null = null;
  let intensity: number | null = null;
  if (def.type === 'character') {
    if (def.ability) {
      effect = def.ability.effect;
      intensity = def.ability.intensity ?? null;
    }
  } else {
    effect = def.effect;
    intensity = def.intensity ?? null;
  }
  if (effect === null || intensity === null) return 0;
  return POWER_VALUED_EFFECTS.has(effect) ? intensity / 100 : intensity;
}

// ── Persisted UI prefs ────────────────────────────────────────────
//
// Slider position + view-mode survive across sessions in localStorage.
// `sizeStep` ∈ [1..5]: smaller = denser display.
// In tile mode, the step picks a card-min-width (CSS auto-fill min);
// in row mode, it picks a row width + height + font size.

interface DeckBuilderPrefs { sizeStep: number; viewMode: 'tile' | 'row'; }
const PREFS_KEY = 'cardverse-deck-prefs';
const PREFS_DEFAULT: DeckBuilderPrefs = { sizeStep: 3, viewMode: 'tile' };
// Tile mode: a single per-step scale that drives card width, height, and
// every text element inside the card via CSS calc(). The CSS baseline (1.0)
// is the prior 110×165px tile.
const TILE_CARD_SCALE = [0.85, 1.0, 1.25, 1.5, 1.8];
// Row mode: the row gets taller and its font scales up at higher steps. The
// per-step row width is a target, but CSS clamps to 1 or 2 columns based on
// screen width and step.
const ROW_HEIGHT_FOR  = [24, 30, 36, 42, 50];
const ROW_FS_FOR      = [11, 12, 14, 16, 18];

function loadDeckPrefs(): DeckBuilderPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...PREFS_DEFAULT };
    const parsed = JSON.parse(raw);
    return {
      sizeStep: typeof parsed.sizeStep === 'number'
                && parsed.sizeStep >= 1 && parsed.sizeStep <= 5
        ? parsed.sizeStep : PREFS_DEFAULT.sizeStep,
      viewMode: parsed.viewMode === 'row' ? 'row' : 'tile',
    };
  } catch { return { ...PREFS_DEFAULT }; }
}

function saveDeckPrefs(p: DeckBuilderPrefs): void {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Format the spirit value with an explicit sign (`+2✦` / `0✦` / `-1✦`).
 *  ✦ is colored blue via the surrounding `.row-spirit` class. */
function formatSpirit(n: number): string {
  if (n === 0) return '0✦';
  return `${n > 0 ? '+' : ''}${n}✦`;
}

/** Build a histogram inside `host` from `values`. Values are bucketed into
 *  bins from `min` to `max` (inclusive) at `binSize` increments; empty
 *  leading and trailing bins are trimmed so the chart focuses on the
 *  occupied range. Bar height is normalized against the tallest bin so the
 *  chart fills its container regardless of count magnitude. */
function renderHistogram(
  host: HTMLElement,
  values: number[],
  opts: { binSize: number; min: number; max: number; formatBin: (v: number) => string },
): void {
  const { binSize, min, max, formatBin } = opts;
  const binCount = Math.floor((max - min) / binSize) + 1;
  const counts = new Array(binCount).fill(0);
  for (const v of values) {
    const idx = Math.max(0, Math.min(binCount - 1, Math.floor((v - min) / binSize)));
    counts[idx]++;
  }
  // Trim empty leading / trailing bins so the rendered chart hugs the data.
  let lo = 0;
  while (lo < counts.length && counts[lo] === 0) lo++;
  let hi = counts.length - 1;
  while (hi >= 0 && counts[hi] === 0) hi--;
  if (lo > hi) {
    host.innerHTML = `<div class="analyze-empty">No cards</div>`;
    return;
  }
  const peak = Math.max(...counts.slice(lo, hi + 1), 1);
  const bars: string[] = [];
  for (let i = lo; i <= hi; i++) {
    const value = min + i * binSize;
    const empty = counts[i] === 0;
    const pct = empty ? 0 : (counts[i] / peak) * 100;
    const labelClass = empty ? 'analyze-bin-label empty' : 'analyze-bin-label';
    // Skip the bar element entirely for zero bins so its CSS min-height
    // (used to keep short bars tall enough to contain the label) doesn't
    // accidentally fill empty bins.
    const barHtml = empty ? '' : `<div class="analyze-bin-bar" style="height: ${pct}%"></div>`;
    bars.push(`
      <div class="analyze-bin">
        <div class="analyze-bin-count">${empty ? '' : counts[i]}</div>
        <div class="analyze-bin-track">
          ${barHtml}
          <span class="${labelClass}">${formatBin(value)}</span>
        </div>
      </div>
    `);
  }
  host.innerHTML = bars.join('');
}

/** Player-facing ability text + icon for row-view. Returns the trigger icon
 *  separate from the body so CSS can hide the body alone on narrow screens
 *  (the icon still gives the player a hint of what kind of ability it is). */
function rowAbilityParts(def: CardDef): { icon: string; text: string } {
  if (def.type === 'action') {
    return { icon: '', text: getActionDescription(def) };
  }
  if (!def.ability) return { icon: '', text: '' };
  const desc = getAbilityDescription(def.ability);
  // EffectText.ts always emits "<Trigger>: <body>." — strip up to and
  // including the first ": ". Fall back to the description as-is if the
  // format ever changes.
  const colonIdx = desc.indexOf(': ');
  const body = colonIdx >= 0 ? desc.slice(colonIdx + 2) : desc;
  return { icon: getAbilityIcon(def.ability.type), text: body };
}

/** Build the inner HTML for a card-tile in row mode. Color-coded numbers
 *  (red power, yellow clock, blue spirit). The ability cell is split into
 *  an icon span and a text span so narrow viewports can hide the text and
 *  keep just the icon. Used by both collection and deck panels with a
 *  different right-side count label.
 *
 *  Action cards have no power, but we still emit an empty `.row-power`
 *  span so the grid template's column count stays consistent — otherwise
 *  the clock/spirit columns would slide left for action rows and the
 *  numbers would no longer line up across rows. */
function renderRowMarkup(def: CardDef, countLabel: string): string {
  const powerText = def.type === 'character' ? String(getCardPower(def)) : '';
  const ability = rowAbilityParts(def);
  const abilityIcon = ability.icon ? `<span class="row-ability-icon">${ability.icon}</span>` : '';
  const abilityText = ability.text ? `<span class="row-ability-text">${escapeHtml(ability.text)}</span>` : '';
  return `
    <span class="row-name">${escapeHtml(def.name)}</span>
    <span class="row-stat row-power">${powerText}</span>
    <span class="row-stat row-clock">⏱${getCardClock(def)}</span>
    <span class="row-stat row-spirit">${formatSpirit(getCardSpirit(def))}</span>
    <span class="row-ability">${abilityIcon}${abilityText}</span>
    <span class="row-stat row-count">${countLabel}</span>
  `;
}

/** Build a tile-mode card with no `<img>` element, then queue its art
 *  through the throttled image loader. Returns the card DOM and the
 *  outstanding request handle so the caller can cancel it on dehydrate.
 *  When the load lands, a clone of the cached `HTMLImageElement` is
 *  inserted ahead of the placeholder and the placeholder is hidden. */
function buildTileCardWithDeferredArt(def: CardDef): { card: HTMLElement; requests: ImageRequest[] } {
  const card = createCardElement(def, undefined, false, undefined, false, 0, true, true, true);
  const requests: ImageRequest[] = [];
  if (!def.art) return { card, requests };

  const artEl = card.querySelector('.card-art') as HTMLElement | null;
  const placeholder = artEl?.querySelector('.placeholder-art') as HTMLElement | null;
  if (!artEl) return { card, requests };

  const req = requestImage(def.art, (img) => {
    if (!img) return;                              // load failed; placeholder stays.
    const clone = img.cloneNode() as HTMLImageElement;
    clone.alt = def.name;
    clone.draggable = false;
    // Defensive: if the underlying HTTP-cache entry was evicted between
    // when we cached the decoded element and when this clone tries to
    // render, fall back to the placeholder rather than a broken-image icon.
    clone.onerror = () => {
      clone.style.display = 'none';
      if (placeholder) placeholder.style.display = '';
    };
    artEl.insertBefore(clone, artEl.firstChild);
    if (placeholder) placeholder.style.display = 'none';
  });
  requests.push(req);
  return { card, requests };
}

/** Compare two cards by a single sort key, returning the standard
 *  -negative / 0 / +positive contract. Used by `sortCards` for both the
 *  primary and the secondary (tiebreaker) keys. */
function compareByKey<T extends { id: string; def: CardDef }>(
  key: SortKey,
  a: T,
  b: T,
  acquiredAt?: Record<string, string>,
): number {
  switch (key) {
    case 'rarity':
      return (RARITY_ORDER[a.def.rarity] ?? 9) - (RARITY_ORDER[b.def.rarity] ?? 9);
    case 'power':
      return getCardPower(b.def) - getCardPower(a.def);
    case 'spirit':
      return getCardSpirit(b.def) - getCardSpirit(a.def);
    case 'clock':
      return getCardClock(a.def) - getCardClock(b.def);
    case 'intensity':
      return getCardIntensity(b.def) - getCardIntensity(a.def);
    case 'type':
      if (a.def.type === b.def.type) return 0;
      return a.def.type === 'character' ? -1 : 1;
    case 'name':
      return a.def.name.localeCompare(b.def.name);
    case 'acquired': {
      const ta = acquiredAt?.[a.id] ?? '';
      const tb = acquiredAt?.[b.id] ?? '';
      return tb.localeCompare(ta);
    }
    default:
      return 0;
  }
}

function sortCards<T extends { id: string; def: CardDef }>(
  items: T[],
  primary: SortKey,
  secondary: SortKey,
  acquiredAt?: Record<string, string>,
): T[] {
  return [...items].sort((a, b) => {
    let d = compareByKey(primary, a, b, acquiredAt);
    if (d === 0 && secondary !== primary) {
      d = compareByKey(secondary, a, b, acquiredAt);
    }
    // Final tiebreak by name so equal-on-both-keys cards still have a
    // stable, predictable order. Skipped when name is already in the chain.
    if (d === 0 && primary !== 'name' && secondary !== 'name') {
      d = a.def.name.localeCompare(b.def.name);
    }
    return d;
  });
}

export function createDeckBuilderView(
  container: HTMLElement,
  playerData: PlayerData,
  catalog: Map<string, CardDef>,
  deckIndex: number,
  onSave: (deck: DeckDef) => void,
  onBack: () => void,
  /** Optional. When provided AND the player has more than one deck,
   *  a "Delete" button appears next to Rename in the header. The
   *  callback fires AFTER the player confirms — caller is responsible
   *  for the actual delete + navigation. */
  onDelete?: () => void,
): void {
  container.innerHTML = '';
  const deck = playerData.decks[deckIndex];
  if (!deck) {
    container.innerHTML = '<div class="menu-screen"><p>No deck found.</p></div>';
    return;
  }

  const workingDeck: DeckDef = {
    name: deck.name,
    cards: deck.cards.map(c => ({ ...c })),
  };

  // Snapshot used by the Back-button unsaved-changes check. Normalized so
  // {id,count} order doesn't matter — the same deck contents in a different
  // array order is not "dirty".
  const normalizeDeck = (d: DeckDef): string => {
    const cards = d.cards
      .filter(c => c.count > 0)
      .map(c => ({ id: c.id, count: c.count }))
      .sort((a, b) => a.id.localeCompare(b.id));
    return JSON.stringify({ name: d.name, cards });
  };
  const initialDeckSnapshot = normalizeDeck(deck);
  const isDeckDirty = (): boolean => normalizeDeck(workingDeck) !== initialDeckSnapshot;
  const canSaveDeck = (): boolean => getDeckCardCount(workingDeck) === getConfig().minDeckSize;

  const el = document.createElement('div');
  el.className = 'deck-builder';
  container.appendChild(el);

  // Rarity filter: multi-select Set instead of single-value so the
  // collection can be narrowed to e.g. just commons (for the planned
  // Pauper rule) or any other rarity combination. Initialized with
  // every rarity selected so the default behavior is "show all".
  const ALL_RARITIES: readonly Rarity[] = ['common', 'uncommon', 'rare', 'legendary'];
  const filterRarities = new Set<Rarity>(ALL_RARITIES);
  let filterType = 'all';
  let collectionSort: SortKey = 'rarity';
  let collectionSecondary: SortKey = 'name';
  let deckSort: SortKey = 'type';
  let deckSecondary: SortKey = 'name';

  // Latest filtered+sorted card-ID orderings from each grid. Captured by
  // renderCollection / renderDeck so the detail modal can navigate
  // prev/next through the same list the player is looking at — and stay
  // in sync with filter, sort, and deck changes.
  let lastCollectionOrder: string[] = [];
  let lastDeckOrder: string[] = [];

  const prefs: DeckBuilderPrefs = loadDeckPrefs();

  // Push the current sizeStep + viewMode into CSS vars + classes on the two
  // grids. Called after the initial render and after every prefs mutation.
  const applyPrefs = (): void => {
    const collection = el.querySelector('#collection-grid') as HTMLElement | null;
    const deckGrid = el.querySelector('#deck-list') as HTMLElement | null;
    const idx = prefs.sizeStep - 1;
    for (const grid of [collection, deckGrid]) {
      if (!grid) continue;
      grid.classList.toggle('view-row', prefs.viewMode === 'row');
      if (prefs.viewMode === 'row') {
        grid.style.setProperty('--cb-row-height', `${ROW_HEIGHT_FOR[idx]}px`);
        grid.style.setProperty('--cb-row-fs', `${ROW_FS_FOR[idx]}px`);
        grid.style.removeProperty('--cb-card-scale-set');
      } else {
        grid.style.setProperty('--cb-card-scale-set', String(TILE_CARD_SCALE[idx]));
        grid.style.removeProperty('--cb-row-height');
        grid.style.removeProperty('--cb-row-fs');
      }
    }
    // Two-column collection only at the two smallest steps — and only on
    // wider screens (the CSS @media gate handles the screen-width side).
    if (collection) {
      collection.classList.toggle(
        'row-cols-2',
        prefs.viewMode === 'row' && prefs.sizeStep <= 2,
      );
    }
  };

  // Gather all unique tags, effect slugs, trigger types, and targets from
  // the player's collection — populates the four filter dropdowns.
  const allTags = new Set<string>();
  const allEffects = new Set<string>();
  const allTriggers = new Set<AbilityType>();
  const allTargets = new Set<Target>();
  for (const [cardId, count] of Object.entries(playerData.collection)) {
    if (count <= 0) continue;
    const def = catalog.get(cardId);
    if (!def) continue;
    for (const t of def.tags) allTags.add(t);
    const slug = getCardEffect(def);
    if (slug) allEffects.add(slug);
    const trig = getCardAbilityType(def);
    if (trig) allTriggers.add(trig);
    const tgt = getCardTarget(def);
    if (tgt) allTargets.add(tgt);
  }
  const sortedTags = [...allTags].sort();
  const filterTags = new Set<string>(sortedTags);
  /** OR = card must have at least one selected tag (current default).
   *  AND = card must have every selected tag. */
  let tagMatchMode: 'or' | 'and' = 'or';
  const sortedEffects = [...allEffects].sort((a, b) => effectLabel(a).localeCompare(effectLabel(b)));
  const filterEffects = new Set<string>(sortedEffects);
  // Triggers and targets follow a fixed lifecycle/spatial ordering rather
  // than alphabetical — see ABILITY_TYPE_ORDER / TARGET_ORDER above.
  const sortedTriggers = ABILITY_TYPE_ORDER.filter(t => allTriggers.has(t));
  const filterTriggers = new Set<AbilityType>(sortedTriggers);
  const sortedTargets = TARGET_ORDER.filter(t => allTargets.has(t));
  const filterTargets = new Set<Target>(sortedTargets);

  // Lazy-grid observers for the collection and deck panels. Each observer
  // hydrates slots when they enter (a generous rootMargin past the visible
  // viewport) and dehydrates when they leave — so a 1500-card collection
  // only ever has the visible window's worth of full card DOM.
  let collectionObserver: IntersectionObserver | null = null;
  let deckObserver: IntersectionObserver | null = null;
  // `requests`: image-loader handles for this slot's currently-pending art
  // loads. Cleared on dehydrate to abort in-flight fetches and free a
  // connection-pool slot for whatever the user does next.
  const slotData = new WeakMap<Element, { hydrate: () => void; requests: ImageRequest[] }>();

  function render() {
    el.innerHTML = `
      <div class="deck-builder-header">
        <button class="menu-btn" id="db-back">Back</button>
        <h2 class="db-deck-name" id="db-deck-name" title="Tap to rename">${workingDeck.name}</h2>
        ${onDelete && playerData.decks.length > 1
          ? '<button class="menu-btn db-rename-btn danger" id="db-delete" title="Delete deck">Delete</button>'
          : ''}
        <span class="deck-counter" id="db-counter"></span>
        <button class="menu-btn primary-green" id="db-save">Save</button>
      </div>
      <div class="deck-builder-body">
        <div class="collection-panel">
          <div class="collection-filters">
            <h3>Collection</h3>
              <div class="tag-filter-dropdown" id="rarity-filter-dropdown">
                <button class="tag-filter-btn" id="rarity-filter-btn">Rarities ▾</button>
                <div class="tag-filter-menu" id="rarity-filter-menu"></div>
              </div>
              <select id="filter-type">
                <option value="all">All Types</option>
                <option value="character" ${filterType === 'character' ? 'selected' : ''}>Characters</option>
                <option value="action" ${filterType === 'action' ? 'selected' : ''}>Actions</option>
              </select>
              <select id="sort-collection">
                <option value="rarity" ${collectionSort === 'rarity' ? 'selected' : ''}>Sort: Rarity</option>
                <option value="power" ${collectionSort === 'power' ? 'selected' : ''}>Sort: Power</option>
                <option value="spirit" ${collectionSort === 'spirit' ? 'selected' : ''}>Sort: Spirit</option>
                <option value="clock" ${collectionSort === 'clock' ? 'selected' : ''}>Sort: Clock</option>
                <option value="intensity" ${collectionSort === 'intensity' ? 'selected' : ''}>Sort: Intensity</option>
                <option value="type" ${collectionSort === 'type' ? 'selected' : ''}>Sort: Type</option>
                <option value="name" ${collectionSort === 'name' ? 'selected' : ''}>Sort: Name</option>
                <option value="acquired" ${collectionSort === 'acquired' ? 'selected' : ''}>Sort: Acquired</option>
              </select>
              <select id="sort-collection-2">
                <option value="rarity" ${collectionSecondary === 'rarity' ? 'selected' : ''}>Then: Rarity</option>
                <option value="power" ${collectionSecondary === 'power' ? 'selected' : ''}>Then: Power</option>
                <option value="spirit" ${collectionSecondary === 'spirit' ? 'selected' : ''}>Then: Spirit</option>
                <option value="clock" ${collectionSecondary === 'clock' ? 'selected' : ''}>Then: Clock</option>
                <option value="intensity" ${collectionSecondary === 'intensity' ? 'selected' : ''}>Then: Intensity</option>
                <option value="type" ${collectionSecondary === 'type' ? 'selected' : ''}>Then: Type</option>
                <option value="name" ${collectionSecondary === 'name' ? 'selected' : ''}>Then: Name</option>
                <option value="acquired" ${collectionSecondary === 'acquired' ? 'selected' : ''}>Then: Acquired</option>
              </select>
              <div class="tag-filter-dropdown" id="tag-filter-dropdown">
                <button class="tag-filter-btn" id="tag-filter-btn">Tags ▾</button>
                <div class="tag-filter-menu" id="tag-filter-menu"></div>
              </div>
              <div class="tag-filter-dropdown" id="effect-filter-dropdown">
                <button class="tag-filter-btn" id="effect-filter-btn">Abilities ▾</button>
                <div class="tag-filter-menu" id="effect-filter-menu"></div>
              </div>
              <div class="tag-filter-dropdown" id="trigger-filter-dropdown">
                <button class="tag-filter-btn" id="trigger-filter-btn">Triggers ▾</button>
                <div class="tag-filter-menu" id="trigger-filter-menu"></div>
              </div>
              <div class="tag-filter-dropdown" id="target-filter-dropdown">
                <button class="tag-filter-btn" id="target-filter-btn">Targets ▾</button>
                <div class="tag-filter-menu" id="target-filter-menu"></div>
              </div>
              <label class="cb-size-slider">
                Size <input type="range" id="db-size" min="1" max="5" step="1" value="${prefs.sizeStep}">
              </label>
              <label class="cb-row-toggle">
                <input type="checkbox" id="db-rowmode" ${prefs.viewMode === 'row' ? 'checked' : ''}>
                Row view
              </label>
          </div>
          <div class="collection-grid" id="collection-grid"></div>
        </div>
        <div class="deck-panel">
          <div class="deck-panel-header">
            <h3>Deck</h3>
            <button type="button" class="menu-btn-sm" id="db-clear">Clear</button>
            <button type="button" class="menu-btn-sm" id="db-analyze">Analyze</button>
            <select id="sort-deck">
              <option value="type" ${deckSort === 'type' ? 'selected' : ''}>Sort: Type</option>
              <option value="rarity" ${deckSort === 'rarity' ? 'selected' : ''}>Sort: Rarity</option>
              <option value="power" ${deckSort === 'power' ? 'selected' : ''}>Sort: Power</option>
              <option value="spirit" ${deckSort === 'spirit' ? 'selected' : ''}>Sort: Spirit</option>
              <option value="clock" ${deckSort === 'clock' ? 'selected' : ''}>Sort: Clock</option>
              <option value="intensity" ${deckSort === 'intensity' ? 'selected' : ''}>Sort: Intensity</option>
              <option value="name" ${deckSort === 'name' ? 'selected' : ''}>Sort: Name</option>
            </select>
            <select id="sort-deck-2">
              <option value="name" ${deckSecondary === 'name' ? 'selected' : ''}>Then: Name</option>
              <option value="rarity" ${deckSecondary === 'rarity' ? 'selected' : ''}>Then: Rarity</option>
              <option value="power" ${deckSecondary === 'power' ? 'selected' : ''}>Then: Power</option>
              <option value="spirit" ${deckSecondary === 'spirit' ? 'selected' : ''}>Then: Spirit</option>
              <option value="clock" ${deckSecondary === 'clock' ? 'selected' : ''}>Then: Clock</option>
              <option value="intensity" ${deckSecondary === 'intensity' ? 'selected' : ''}>Then: Intensity</option>
              <option value="type" ${deckSecondary === 'type' ? 'selected' : ''}>Then: Type</option>
            </select>
          </div>
          <div class="deck-list" id="deck-list"></div>
        </div>
      </div>
    `;

    el.querySelector('#db-back')!.addEventListener('click', () => {
      if (!isDeckDirty()) {
        onBack();
        return;
      }
      openUnsavedChangesConfirm();
    });
    el.querySelector('#db-save')!.addEventListener('click', () => {
      if (canSaveDeck()) onSave(workingDeck);
    });
    el.querySelector('#db-deck-name')!.addEventListener('click', () => {
      showPrompt({
        title: 'Rename deck',
        defaultValue: workingDeck.name,
        confirmLabel: 'Rename',
        onConfirm: (newName) => {
          workingDeck.name = newName;
          const nameEl = el.querySelector('#db-deck-name');
          if (nameEl) nameEl.textContent = workingDeck.name;
        },
      });
    });
    el.querySelector('#db-delete')?.addEventListener('click', () => {
      onDelete?.();
    });

    el.querySelector('#filter-type')!.addEventListener('change', (e) => {
      filterType = (e.target as HTMLSelectElement).value;
      renderCollection();
    });
    el.querySelector('#sort-collection')!.addEventListener('change', (e) => {
      collectionSort = (e.target as HTMLSelectElement).value as SortKey;
      renderCollection();
    });
    el.querySelector('#sort-collection-2')!.addEventListener('change', (e) => {
      collectionSecondary = (e.target as HTMLSelectElement).value as SortKey;
      renderCollection();
    });

    const sizeInput = el.querySelector('#db-size') as HTMLInputElement;
    sizeInput.addEventListener('input', () => {
      prefs.sizeStep = Number(sizeInput.value);
      saveDeckPrefs(prefs);
      applyPrefs();
    });
    const rowInput = el.querySelector('#db-rowmode') as HTMLInputElement;
    rowInput.addEventListener('change', () => {
      prefs.viewMode = rowInput.checked ? 'row' : 'tile';
      saveDeckPrefs(prefs);
      applyPrefs();
      renderCollection();
      renderDeck();
    });


    // All four filter menus are mutually exclusive — opening one closes the
    // others so they don't stack on top of each other in narrow layouts.
    const ALL_FILTER_MENU_IDS = [
      '#rarity-filter-menu',
      '#tag-filter-menu',
      '#effect-filter-menu',
      '#trigger-filter-menu',
      '#target-filter-menu',
    ];
    const closeOtherFilterMenus = (keepId: string): void => {
      for (const id of ALL_FILTER_MENU_IDS) {
        if (id === keepId) continue;
        el.querySelector(id)?.classList.remove('open');
      }
    };

    // Rarity filter dropdown — multi-select checkbox style (mirrors the
    // tag / effect / trigger / target filters). Useful for the planned
    // Pauper rule (commons only) and any future rarity-restricted
    // formats. Defaults to all four rarities selected, so the player
    // sees their full collection unless they narrow it.
    const rarityBtn = el.querySelector('#rarity-filter-btn')!;
    const rarityMenu = el.querySelector('#rarity-filter-menu') as HTMLElement;

    rarityBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeOtherFilterMenus('#rarity-filter-menu');
      rarityMenu.classList.toggle('open');
      if (rarityMenu.classList.contains('open')) positionDropdown(rarityMenu);
    });
    document.addEventListener('click', () => rarityMenu.classList.remove('open'));
    rarityMenu.addEventListener('click', (e) => e.stopPropagation());

    const rarityLabel = (r: Rarity): string => r.charAt(0).toUpperCase() + r.slice(1);

    function renderRarityMenu() {
      const allChecked = filterRarities.size === ALL_RARITIES.length;
      rarityMenu.innerHTML = `
        <label class="tag-filter-item">
          <input type="checkbox" data-rarity="__all__" ${allChecked ? 'checked' : ''} /> All
        </label>
        ${ALL_RARITIES.map(r => `
          <label class="tag-filter-item">
            <input type="checkbox" data-rarity="${r}" ${filterRarities.has(r) ? 'checked' : ''} /> ${rarityLabel(r)}
          </label>
        `).join('')}
      `;
      rarityMenu.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
          const r = (cb as HTMLInputElement).dataset.rarity!;
          if (r === '__all__') {
            if ((cb as HTMLInputElement).checked) {
              for (const each of ALL_RARITIES) filterRarities.add(each);
            } else {
              filterRarities.clear();
            }
          } else {
            if ((cb as HTMLInputElement).checked) {
              filterRarities.add(r as Rarity);
            } else {
              filterRarities.delete(r as Rarity);
            }
          }
          renderRarityMenu();
          renderCollection();
        });
      });
      // Button label collapses to "Rarities ▾" when everything's selected;
      // shows the count otherwise so the player can see at a glance
      // that they're filtered.
      rarityBtn.textContent = allChecked
        ? 'Rarities ▾'
        : `Rarities (${filterRarities.size}) ▾`;
    }
    renderRarityMenu();

    // Tag filter dropdown
    const tagBtn = el.querySelector('#tag-filter-btn')!;
    const tagMenu = el.querySelector('#tag-filter-menu') as HTMLElement;

    tagBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeOtherFilterMenus('#tag-filter-menu');
      tagMenu.classList.toggle('open');
      if (tagMenu.classList.contains('open')) positionDropdown(tagMenu);
    });
    document.addEventListener('click', () => tagMenu.classList.remove('open'));
    tagMenu.addEventListener('click', (e) => e.stopPropagation());

    function renderTagMenu() {
      const allChecked = filterTags.size === sortedTags.length;
      tagMenu.innerHTML = `
        <div class="tag-filter-header">
          <label class="tag-filter-item">
            <input type="checkbox" data-tag="__all__" ${allChecked ? 'checked' : ''} /> All
          </label>
          <div class="tag-filter-mode" role="group" aria-label="Match mode">
            <button type="button" data-mode="or" class="${tagMatchMode === 'or' ? 'active' : ''}">Or</button>
            <button type="button" data-mode="and" class="${tagMatchMode === 'and' ? 'active' : ''}">And</button>
          </div>
        </div>
        ${sortedTags.map(t => `
          <label class="tag-filter-item">
            <input type="checkbox" data-tag="${t}" ${filterTags.has(t) ? 'checked' : ''} /> ${t}
          </label>
        `).join('')}
      `;
      tagMenu.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
          const tag = (cb as HTMLInputElement).dataset.tag!;
          if (tag === '__all__') {
            if ((cb as HTMLInputElement).checked) {
              for (const t of sortedTags) filterTags.add(t);
            } else {
              filterTags.clear();
            }
          } else {
            if ((cb as HTMLInputElement).checked) {
              filterTags.add(tag);
            } else {
              filterTags.delete(tag);
            }
          }
          renderTagMenu();
          renderCollection();
        });
      });
      tagMenu.querySelectorAll<HTMLButtonElement>('.tag-filter-mode button').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          tagMatchMode = btn.dataset.mode === 'and' ? 'and' : 'or';
          renderTagMenu();
          renderCollection();
        });
      });
      // Update button label — show the mode in parentheses when narrowed.
      const modeTag = tagMatchMode === 'and' ? ' ∧' : '';
      tagBtn.textContent = allChecked
        ? 'Tags ▾'
        : `Tags (${filterTags.size}${modeTag}) ▾`;
    }
    renderTagMenu();

    // Ability filter dropdown
    const effectBtn = el.querySelector('#effect-filter-btn')!;
    const effectMenu = el.querySelector('#effect-filter-menu') as HTMLElement;

    effectBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeOtherFilterMenus('#effect-filter-menu');
      effectMenu.classList.toggle('open');
      if (effectMenu.classList.contains('open')) positionDropdown(effectMenu);
    });
    document.addEventListener('click', () => effectMenu.classList.remove('open'));
    effectMenu.addEventListener('click', (e) => e.stopPropagation());

    function renderEffectMenu() {
      const allChecked = filterEffects.size === sortedEffects.length;
      effectMenu.innerHTML = `
        <label class="tag-filter-item">
          <input type="checkbox" data-effect="__all__" ${allChecked ? 'checked' : ''} /> All
        </label>
        ${sortedEffects.map(e => `
          <label class="tag-filter-item">
            <input type="checkbox" data-effect="${e}" ${filterEffects.has(e) ? 'checked' : ''} /> ${effectLabel(e)}
          </label>
        `).join('')}
      `;
      effectMenu.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
          const slug = (cb as HTMLInputElement).dataset.effect!;
          if (slug === '__all__') {
            if ((cb as HTMLInputElement).checked) {
              for (const e of sortedEffects) filterEffects.add(e);
            } else {
              filterEffects.clear();
            }
          } else {
            if ((cb as HTMLInputElement).checked) {
              filterEffects.add(slug);
            } else {
              filterEffects.delete(slug);
            }
          }
          renderEffectMenu();
          renderCollection();
        });
      });
      effectBtn.textContent = allChecked ? 'Abilities ▾' : `Abilities (${filterEffects.size}) ▾`;
    }
    renderEffectMenu();

    // Trigger filter dropdown — only character abilities have a trigger
    // type, so narrowing this filter excludes action cards entirely.
    const triggerBtn = el.querySelector('#trigger-filter-btn')!;
    const triggerMenu = el.querySelector('#trigger-filter-menu') as HTMLElement;

    triggerBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeOtherFilterMenus('#trigger-filter-menu');
      triggerMenu.classList.toggle('open');
      if (triggerMenu.classList.contains('open')) positionDropdown(triggerMenu);
    });
    document.addEventListener('click', () => triggerMenu.classList.remove('open'));
    triggerMenu.addEventListener('click', (e) => e.stopPropagation());

    function renderTriggerMenu() {
      const allChecked = filterTriggers.size === sortedTriggers.length;
      triggerMenu.innerHTML = `
        <label class="tag-filter-item">
          <input type="checkbox" data-trigger="__all__" ${allChecked ? 'checked' : ''} /> All
        </label>
        ${sortedTriggers.map(t => `
          <label class="tag-filter-item">
            <input type="checkbox" data-trigger="${t}" ${filterTriggers.has(t) ? 'checked' : ''} /> ${ABILITY_TYPE_LABEL[t]}
          </label>
        `).join('')}
      `;
      triggerMenu.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
          const trig = (cb as HTMLInputElement).dataset.trigger! as AbilityType | '__all__';
          if (trig === '__all__') {
            if ((cb as HTMLInputElement).checked) {
              for (const t of sortedTriggers) filterTriggers.add(t);
            } else {
              filterTriggers.clear();
            }
          } else {
            if ((cb as HTMLInputElement).checked) {
              filterTriggers.add(trig);
            } else {
              filterTriggers.delete(trig);
            }
          }
          renderTriggerMenu();
          renderCollection();
        });
      });
      triggerBtn.textContent = allChecked ? 'Triggers ▾' : `Triggers (${filterTriggers.size}) ▾`;
    }
    renderTriggerMenu();

    // Target filter dropdown — only verb-based abilities/effects have a
    // target, so narrowing this filter excludes atomic-slug effects and
    // characters without abilities.
    const targetBtn = el.querySelector('#target-filter-btn')!;
    const targetMenu = el.querySelector('#target-filter-menu') as HTMLElement;

    targetBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeOtherFilterMenus('#target-filter-menu');
      targetMenu.classList.toggle('open');
      if (targetMenu.classList.contains('open')) positionDropdown(targetMenu);
    });
    document.addEventListener('click', () => targetMenu.classList.remove('open'));
    targetMenu.addEventListener('click', (e) => e.stopPropagation());

    function renderTargetMenu() {
      const allChecked = filterTargets.size === sortedTargets.length;
      targetMenu.innerHTML = `
        <label class="tag-filter-item">
          <input type="checkbox" data-target="__all__" ${allChecked ? 'checked' : ''} /> All
        </label>
        ${sortedTargets.map(t => `
          <label class="tag-filter-item">
            <input type="checkbox" data-target="${t}" ${filterTargets.has(t) ? 'checked' : ''} /> ${TARGET_LABEL[t]}
          </label>
        `).join('')}
      `;
      targetMenu.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
          const tgt = (cb as HTMLInputElement).dataset.target! as Target | '__all__';
          if (tgt === '__all__') {
            if ((cb as HTMLInputElement).checked) {
              for (const t of sortedTargets) filterTargets.add(t);
            } else {
              filterTargets.clear();
            }
          } else {
            if ((cb as HTMLInputElement).checked) {
              filterTargets.add(tgt);
            } else {
              filterTargets.delete(tgt);
            }
          }
          renderTargetMenu();
          renderCollection();
        });
      });
      targetBtn.textContent = allChecked ? 'Targets ▾' : `Targets (${filterTargets.size}) ▾`;
    }
    renderTargetMenu();

    el.querySelector('#sort-deck')!.addEventListener('change', (e) => {
      deckSort = (e.target as HTMLSelectElement).value as SortKey;
      renderDeck();
    });
    el.querySelector('#sort-deck-2')!.addEventListener('change', (e) => {
      deckSecondary = (e.target as HTMLSelectElement).value as SortKey;
      renderDeck();
    });

    el.querySelector('#db-analyze')!.addEventListener('click', () => openDeckAnalyze());
    el.querySelector('#db-clear')!.addEventListener('click', () => openClearConfirm());

    // Drop target: deck panel accepts drags from collection tiles.
    const deckList = el.querySelector('#deck-list') as HTMLElement;
    deckList.addEventListener('dragover', (e) => {
      if (!e.dataTransfer?.types.includes(DRAG_MIME)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      deckList.classList.add('drop-target-active');
    });
    deckList.addEventListener('dragleave', (e) => {
      // Only clear when leaving the deck-list itself, not when crossing
      // into a child element.
      if (e.target === deckList) deckList.classList.remove('drop-target-active');
    });
    deckList.addEventListener('drop', (e) => {
      const id = e.dataTransfer?.getData(DRAG_MIME);
      deckList.classList.remove('drop-target-active');
      if (!id) return;
      e.preventDefault();
      tryAddCard(id);
    });

    // Drop target: collection panel accepts drags from deck tiles to remove.
    const collectionGrid = el.querySelector('#collection-grid') as HTMLElement;
    collectionGrid.addEventListener('dragover', (e) => {
      if (!e.dataTransfer?.types.includes(DRAG_MIME_REMOVE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      collectionGrid.classList.add('drop-target-active');
    });
    collectionGrid.addEventListener('dragleave', (e) => {
      if (e.target === collectionGrid) collectionGrid.classList.remove('drop-target-active');
    });
    collectionGrid.addEventListener('drop', (e) => {
      const id = e.dataTransfer?.getData(DRAG_MIME_REMOVE);
      collectionGrid.classList.remove('drop-target-active');
      if (!id) return;
      e.preventDefault();
      removeCardFromDeck(workingDeck, id);
      renderCollection();
      renderDeck();
      updateCounter();
    });

    renderCollection();
    renderDeck();
    updateCounter();
    applyPrefs();
  }

  /** Confirmation dialog for the destructive "Clear deck" action. Mirrors
   *  the rest of the modal stack (overlay backdrop, Esc / outside-click to
   *  cancel) so dismissal patterns are consistent. */
  function openClearConfirm(): void {
    if (workingDeck.cards.length === 0) return;
    const overlay = document.createElement('div');
    overlay.className = 'card-detail-overlay';
    overlay.innerHTML = `
      <div class="confirm-modal">
        <h3>Clear deck?</h3>
        <p>This removes every card from <strong>${escapeHtml(workingDeck.name)}</strong>. You can rebuild it before saving.</p>
        <div class="confirm-buttons">
          <button type="button" class="menu-btn-sm" data-action="cancel">Cancel</button>
          <button type="button" class="menu-btn-sm danger" data-action="confirm">Clear deck</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = (): void => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      close();
    });
    overlay.querySelector('[data-action="confirm"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      workingDeck.cards = [];
      renderCollection();
      renderDeck();
      updateCounter();
      close();
    });
    document.addEventListener('keydown', onKey);
  }

  /** Confirmation dialog shown when the player presses Back with unsaved
   *  changes. Cancel is always offered (explicit affordance for the
   *  "keep editing" choice — the scrim/Esc also work), with Save added
   *  in the middle when the deck is in a valid savable state. */
  function openUnsavedChangesConfirm(): void {
    const overlay = document.createElement('div');
    overlay.className = 'card-detail-overlay';
    const saveBtn = canSaveDeck()
      ? '<button type="button" class="menu-btn-sm primary-green" data-action="save">Save</button>'
      : '';
    overlay.innerHTML = `
      <div class="confirm-modal">
        <h3>Unsaved changes</h3>
        <p>You have unsaved changes to <strong>${escapeHtml(workingDeck.name)}</strong>.</p>
        <div class="confirm-buttons">
          <button type="button" class="menu-btn-sm danger" data-action="exit">Exit Anyway</button>
          ${saveBtn}
          <button type="button" class="menu-btn-sm" data-action="cancel">Cancel</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = (): void => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      close();
    });
    overlay.querySelector('[data-action="exit"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      close();
      onBack();
    });
    overlay.querySelector('[data-action="save"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      close();
      onSave(workingDeck);
    });
    document.addEventListener('keydown', onKey);
  }

  /** Modal showing the deck's stat distribution: per-stat histograms +
   *  averages. Reads workingDeck.cards (id + count) and the catalog. */
  function openDeckAnalyze(): void {
    const overlay = document.createElement('div');
    overlay.className = 'card-detail-overlay';
    overlay.innerHTML = `
      <div class="deck-analyze-modal">
        <button class="card-detail-close" type="button" aria-label="Close">×</button>
        <div class="analyze-section analyze-power">
          <div class="analyze-row"><span class="analyze-label">Power</span><span class="analyze-avg" id="an-avg-power"></span></div>
          <div class="analyze-bars" id="an-bars-power"></div>
        </div>
        <div class="analyze-section analyze-clock">
          <div class="analyze-row"><span class="analyze-label">Clock</span><span class="analyze-avg" id="an-avg-clock"></span></div>
          <div class="analyze-bars" id="an-bars-clock"></div>
        </div>
        <div class="analyze-section analyze-spirit">
          <div class="analyze-row"><span class="analyze-label">Spirit</span><span class="analyze-avg" id="an-avg-spirit"></span></div>
          <div class="analyze-bars" id="an-bars-spirit"></div>
        </div>
        <div class="analyze-support">
          <div class="analyze-row"><span class="analyze-label">Support rate</span><span class="analyze-avg" id="an-support"></span></div>
          <div class="support-bar-track"><div class="support-bar-fill" id="an-support-fill"></div></div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    // Expand each card by its count so a 3-of card contributes 3 samples.
    const characters: { power: number; clock: number; spirit: number; count: number }[] = [];
    const all: { clock: number; spirit: number; count: number }[] = [];
    for (const c of workingDeck.cards) {
      const def = catalog.get(c.id);
      if (!def) continue;
      const clock = getCardClock(def);
      const spirit = getCardSpirit(def);
      all.push({ clock, spirit, count: c.count });
      if (def.type === 'character') {
        characters.push({ power: getCardPower(def), clock, spirit, count: c.count });
      }
    }

    // Power bins of 100 — typical premade range is 100..1500. Empty trailing
    // bins are trimmed so the chart doesn't span unused values.
    renderHistogram(
      overlay.querySelector('#an-bars-power') as HTMLElement,
      characters.flatMap(c => Array(c.count).fill(c.power)),
      { binSize: 100, min: 100, max: 1500, formatBin: v => String(v) },
    );
    const totalCharCopies = characters.reduce((s, c) => s + c.count, 0);
    const sumPower = characters.reduce((s, c) => s + c.power * c.count, 0);
    (overlay.querySelector('#an-avg-power') as HTMLElement).innerHTML =
      totalCharCopies > 0
        ? `avg <span class="analyze-avg-value">${Math.round(sumPower / totalCharCopies)}</span>`
        : '—';

    // Clock bins are individual integers 1..12.
    renderHistogram(
      overlay.querySelector('#an-bars-clock') as HTMLElement,
      all.flatMap(c => Array(c.count).fill(c.clock)),
      { binSize: 1, min: 1, max: 12, formatBin: v => String(v) },
    );
    const totalCopies = all.reduce((s, c) => s + c.count, 0);
    const sumClock = all.reduce((s, c) => s + c.clock * c.count, 0);
    (overlay.querySelector('#an-avg-clock') as HTMLElement).innerHTML =
      totalCopies > 0
        ? `avg <span class="analyze-avg-value">${(sumClock / totalCopies).toFixed(1)}</span>`
        : '—';

    // Spirit bins individual integers covering both costs and gains.
    renderHistogram(
      overlay.querySelector('#an-bars-spirit') as HTMLElement,
      all.flatMap(c => Array(c.count).fill(c.spirit)),
      { binSize: 1, min: -5, max: 6, formatBin: v => v > 0 ? `+${v}` : String(v) },
    );
    const sumSpirit = all.reduce((s, c) => s + c.spirit * c.count, 0);
    const avgSpirit = totalCopies > 0 ? sumSpirit / totalCopies : 0;
    (overlay.querySelector('#an-avg-spirit') as HTMLElement).innerHTML =
      totalCopies > 0
        ? `avg <span class="analyze-avg-value">${avgSpirit > 0 ? '+' : ''}${avgSpirit.toFixed(1)}</span>`
        : '—';

    // Support rate: of all unordered pairs of character copies in the deck,
    // what fraction share at least 2 tags (the static support requirement;
    // passive-easy-support / passive-extra-support depend on board state and
    // aren't considered here). Two copies of the same card are a valid pair
    // and trivially share all tags. Action cards aren't part of any pair.
    const charDefs: CardDef[] = [];
    for (const c of workingDeck.cards) {
      const def = catalog.get(c.id);
      if (!def || def.type !== 'character') continue;
      for (let i = 0; i < c.count; i++) charDefs.push(def);
    }
    let supportable = 0;
    let totalPairs = 0;
    for (let i = 0; i < charDefs.length; i++) {
      const tagsA = new Set(charDefs[i].tags);
      for (let j = i + 1; j < charDefs.length; j++) {
        totalPairs++;
        let shared = 0;
        for (const t of charDefs[j].tags) if (tagsA.has(t)) shared++;
        if (shared >= 2) supportable++;
      }
    }
    const rate = totalPairs > 0 ? (supportable / totalPairs) * 100 : 0;
    (overlay.querySelector('#an-support') as HTMLElement).innerHTML =
      totalPairs > 0
        ? `<span class="analyze-avg-value">${rate.toFixed(0)}%</span> <span class="analyze-support-detail">(${supportable}/${totalPairs} pairs)</span>`
        : '—';
    (overlay.querySelector('#an-support-fill') as HTMLElement).style.width = `${rate}%`;

    const close = (): void => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('.card-detail-close')?.addEventListener('click', (e) => {
      e.stopPropagation();
      close();
    });
    document.addEventListener('keydown', onKey);
  }

  /** Full-screen card-detail modal. Replaces the in-grid hover tooltip with
   *  a tap-to-open view that also exposes the +/- controls and prev/next
   *  navigation through the originating list (collection or deck). The
   *  modal is stateful: `currentId` mutates as the player navigates, and
   *  `renderModal()` rebuilds the contents in place so the overlay /
   *  keydown listener stay attached. Disable rules mirror `tryAddCard` /
   *  `removeCardFromDeck` so the modal can never produce an illegal state. */
  function openCardDetail(initialCardId: string, source: 'collection' | 'deck'): void {
    if (!catalog.get(initialCardId)) return;
    const overlay = document.createElement('div');
    overlay.className = 'card-detail-overlay';
    document.body.appendChild(overlay);

    let currentId = initialCardId;

    const getList = (): string[] => source === 'collection' ? lastCollectionOrder : lastDeckOrder;

    const navigate = (delta: number): void => {
      const list = getList();
      const idx = list.indexOf(currentId);
      if (idx < 0) return;
      const next = idx + delta;
      if (next < 0 || next >= list.length) return;
      currentId = list[next];
      renderModal();
    };

    const close = (): void => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'ArrowLeft') { navigate(-1); return; }
      if (e.key === 'ArrowRight') { navigate(1); return; }
    };

    function renderModal(): void {
      const def = catalog.get(currentId);
      if (!def) { close(); return; }

      // Flavor + tags are surfaced here (the modal is the player's primary
      // inspection surface) — both fields are guarded so empty values don't
      // render an empty block / lonely quotes.
      const flavorBlock = def.flavor
        ? `<div class="card-detail-flavor">"${escapeHtml(def.flavor)}"</div>`
        : '';
      const tagsBlock = def.tags.length > 0
        ? `<div class="card-detail-tags">${
            def.tags.map(t => `<span class="card-detail-tag">${escapeHtml(t)}</span>`).join('')
          }</div>`
        : '';
      const infoBlock = (flavorBlock || tagsBlock)
        ? `<div class="card-detail-info">${flavorBlock}${tagsBlock}</div>`
        : '';

      const list = getList();
      const idx = list.indexOf(currentId);
      const hasPrev = idx > 0;
      const hasNext = idx >= 0 && idx < list.length - 1;

      overlay.innerHTML = `
        <div class="card-detail-modal">
          <div class="card-detail-art">
            <div class="card-detail-card-host"></div>
            <button class="card-detail-close" type="button" aria-label="Close">×</button>
          </div>
          ${infoBlock}
          <div class="card-detail-controls">
            <button class="cd-nav cd-prev" type="button" title="Previous card" aria-label="Previous card"${hasPrev ? '' : ' disabled'}>‹</button>
            <div class="cd-mid">
              <button class="cd-minus" type="button" title="Remove one copy">−</button>
              <span class="cd-count"></span>
              <button class="cd-plus" type="button" title="Add one copy">+</button>
            </div>
            <button class="cd-nav cd-next" type="button" title="Next card" aria-label="Next card"${hasNext ? '' : ' disabled'}>›</button>
          </div>
        </div>
      `;

      // Share button — sits in the top-left of the art panel, mirroring the
      // close × in the top-right. Re-created per card because its target id
      // changes when the player navigates.
      const shareBtn = createShareButton(currentId, { compact: true });
      shareBtn.classList.add('card-detail-share');
      overlay.querySelector('.card-detail-art')!.appendChild(shareBtn);

      const cardHost = overlay.querySelector('.card-detail-card-host') as HTMLElement;
      const minusBtn = overlay.querySelector('.cd-minus') as HTMLButtonElement;
      const plusBtn = overlay.querySelector('.cd-plus') as HTMLButtonElement;
      const prevBtn = overlay.querySelector('.cd-prev') as HTMLButtonElement;
      const nextBtn = overlay.querySelector('.cd-next') as HTMLButtonElement;
      const countEl = overlay.querySelector('.cd-count') as HTMLElement;
      const config = getConfig();

      cardHost.replaceChildren(
        createCardElement(def, undefined, false, undefined, false, 0, false, true),
      );
      const owned = playerData.collection[currentId] ?? 0;
      const inDeck = workingDeck.cards.find(c => c.id === currentId)?.count ?? 0;
      const deckTotal = getDeckCardCount(workingDeck);
      const atMax = inDeck >= config.maxCopiesPerCard || inDeck >= owned;
      const deckFull = deckTotal >= DECK_OVER_CAP;
      countEl.textContent = `${inDeck} / ${owned}`;
      minusBtn.disabled = inDeck === 0;
      plusBtn.disabled = atMax || deckFull;

      minusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (minusBtn.disabled) return;
        removeCardFromDeck(workingDeck, currentId);
        renderCollection();
        renderDeck();
        updateCounter();
        // In deck view, removing the last copy drops the card out of the
        // list. Try to land on whichever card now occupies the same slot
        // so the player keeps walking forward; if the list is empty,
        // close.
        if (source === 'deck') {
          const remaining = workingDeck.cards.find(c => c.id === currentId)?.count ?? 0;
          if (remaining === 0) {
            const newList = getList();
            if (newList.length === 0) { close(); return; }
            const fallbackIdx = Math.min(idx, newList.length - 1);
            currentId = newList[Math.max(0, fallbackIdx)];
          }
        }
        renderModal();
      });
      plusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (plusBtn.disabled) return;
        // tryAddCard re-renders the collection / deck / counter on success.
        tryAddCard(currentId);
        renderModal();
      });
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!prevBtn.disabled) navigate(-1);
      });
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!nextBtn.disabled) navigate(1);
      });
      overlay.querySelector('.card-detail-close')?.addEventListener('click', (e) => {
        e.stopPropagation();
        close();
      });
    }

    renderModal();
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', onKey);
  }

  function tryAddCard(id: string): boolean {
    const config = getConfig();
    const owned = playerData.collection[id] ?? 0;
    const inDeck = workingDeck.cards.find(c => c.id === id)?.count ?? 0;
    if (owned <= 0) return false;
    if (inDeck >= config.maxCopiesPerCard) return false;
    if (inDeck >= owned) return false;
    if (getDeckCardCount(workingDeck) >= DECK_OVER_CAP) return false;
    addCardToDeck(workingDeck, id);
    renderCollection();
    renderDeck();
    updateCounter();
    return true;
  }

  /** Build the full card DOM into an empty slot. Called by the
   *  IntersectionObserver when a slot enters the visible window (plus
   *  rootMargin). Idempotent — bails if children already present. */
  function hydrate(slot: HTMLElement): void {
    if (slot.firstChild) return;
    slotData.get(slot)?.hydrate();
  }

  /** Tear the heavy card DOM out of a slot when it scrolls offscreen.
   *  The slot's CSS-set dimensions and persistent click/drag handlers
   *  remain so re-entry is cheap. Also cancels any pending image loads
   *  the slot kicked off — without this, the request stays in the
   *  connection pool even though we no longer care about the result. */
  function dehydrate(slot: HTMLElement): void {
    if (!slot.firstChild) return;
    const data = slotData.get(slot);
    if (data) {
      for (const req of data.requests) req.cancel();
      data.requests.length = 0;
    }
    slot.replaceChildren();
  }

  /** (Re)build a lazy grid: clears the container, creates one slot per
   *  item with a `hydrate` closure, and observes each slot. The observer
   *  is recreated each call so its target list stays in sync. */
  function fillLazyGrid<T>(
    grid: HTMLElement,
    items: T[],
    makeSlot: (item: T) => HTMLElement,
    hydrateFn: (slot: HTMLElement, item: T) => void,
    observerSlot: 'collection' | 'deck',
  ): void {
    if (observerSlot === 'collection') collectionObserver?.disconnect();
    else deckObserver?.disconnect();

    grid.replaceChildren();

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const slot = entry.target as HTMLElement;
        if (entry.isIntersecting) hydrate(slot);
        else dehydrate(slot);
      }
    }, { root: grid, rootMargin: '150px 0px' });

    if (observerSlot === 'collection') collectionObserver = observer;
    else deckObserver = observer;

    const frag = document.createDocumentFragment();
    for (const item of items) {
      const slot = makeSlot(item);
      slotData.set(slot, { hydrate: () => hydrateFn(slot, item), requests: [] });
      frag.appendChild(slot);
    }
    grid.appendChild(frag);

    for (const child of Array.from(grid.children)) {
      observer.observe(child);
    }
  }

  function renderCollection() {
    const grid = el.querySelector('#collection-grid') as HTMLElement;

    let ownedCards: { id: string; def: CardDef; owned: number }[] = [];
    for (const [cardId, count] of Object.entries(playerData.collection)) {
      if (count <= 0) continue;
      const def = catalog.get(cardId);
      if (!def) continue;
      if (!filterRarities.has(def.rarity)) continue;
      if (filterType !== 'all' && def.type !== filterType) continue;
      // Tag filter. OR mode (default): pass if the card has at least one
      // selected tag. AND mode: pass only if the card has every selected
      // tag. AND becomes restrictive fast — typically zero matches with a
      // 3+ tag selection.
      if (filterTags.size < sortedTags.length) {
        if (tagMatchMode === 'and') {
          const cardTags = new Set(def.tags);
          let hasAll = true;
          for (const t of filterTags) {
            if (!cardTags.has(t)) { hasAll = false; break; }
          }
          if (!hasAll) continue;
        } else {
          const hasMatchingTag = def.tags.some(t => filterTags.has(t));
          if (!hasMatchingTag) continue;
        }
      }
      // Ability filter: card's effect slug must be selected. Cards without an
      // effect (plain character, no ability) don't pass when the set is
      // narrowed.
      if (filterEffects.size < sortedEffects.length) {
        const slug = getCardEffect(def);
        if (!slug || !filterEffects.has(slug)) continue;
      }
      // Trigger filter: only character abilities have a trigger type. When
      // narrowed, this excludes action cards and characters without an
      // ability.
      if (filterTriggers.size < sortedTriggers.length) {
        const trig = getCardAbilityType(def);
        if (!trig || !filterTriggers.has(trig)) continue;
      }
      // Target filter: only verb-based abilities/effects carry a target.
      // When narrowed, this excludes atomic-slug effects (e.g. draw) and
      // characters without an ability.
      if (filterTargets.size < sortedTargets.length) {
        const tgt = getCardTarget(def);
        if (!tgt || !filterTargets.has(tgt)) continue;
      }
      ownedCards.push({ id: cardId, def, owned: count });
    }

    ownedCards = sortCards(ownedCards, collectionSort, collectionSecondary, playerData.collectionAcquiredAt);
    const config = getConfig();

    interface CollectionItem { id: string; def: CardDef; owned: number; inDeck: number; disabled: boolean; }
    const items: CollectionItem[] = ownedCards.map(({ id, def, owned }) => {
      const inDeck = workingDeck.cards.find(c => c.id === id)?.count ?? 0;
      const atMax = inDeck >= config.maxCopiesPerCard || inDeck >= owned;
      const deckFull = getDeckCardCount(workingDeck) >= DECK_OVER_CAP;
      return { id, def, owned, inDeck, disabled: atMax || deckFull };
    });
    lastCollectionOrder = items.map(i => i.id);

    fillLazyGrid(grid, items, (item) => {
      const tile = document.createElement('div');
      tile.className = `card-tile${item.disabled ? ' dimmed' : ''}`;
      tile.dataset.cardId = item.id;
      tile.dataset.rarity = item.def.rarity;
      // Click ALWAYS opens the detail modal — even on at-max / deck-full
      // tiles, so the user can see the +/- state and remove copies.
      tile.addEventListener('click', () => openCardDetail(item.id, 'collection'));
      if (!item.disabled) {
        tile.draggable = true;
        tile.addEventListener('dragstart', (e) => {
          if (!e.dataTransfer) return;
          e.dataTransfer.setData(DRAG_MIME, item.id);
          e.dataTransfer.effectAllowed = 'copy';
          tile.classList.add('dragging');
          hideTooltip();
        });
        tile.addEventListener('dragend', () => tile.classList.remove('dragging'));
      }
      return tile;
    }, (tile, item) => {
      if (prefs.viewMode === 'row') {
        tile.classList.add('row');
        tile.innerHTML = renderRowMarkup(item.def, `${item.inDeck}/${item.owned}`);
        return;
      }
      const { card, requests } = buildTileCardWithDeferredArt(item.def);
      tile.appendChild(card);
      slotData.get(tile)?.requests.push(...requests);

      const count = document.createElement('div');
      count.className = 'tile-count';
      count.innerHTML = `<span class="cc-in-deck">${item.inDeck}</span>/${item.owned}`;
      card.appendChild(count);

      if (isCustomCardId(item.id)) {
        const shareBtn = createShareButton(item.id, { compact: true });
        shareBtn.classList.add('tile-share');
        // Append to the tile, not the .card — when the tile is dimmed
        // (at-max / deck-full), opacity on .card would also fade the share
        // button, but the share action stays valid regardless of deck state.
        tile.appendChild(shareBtn);
      }
    }, 'collection');
  }

  function renderDeck() {
    const list = el.querySelector('#deck-list') as HTMLElement;

    let sorted = workingDeck.cards
      .map(c => ({ ...c, def: catalog.get(c.id)! }))
      .filter(c => c.def);

    sorted = sortCards(sorted, deckSort, deckSecondary);
    lastDeckOrder = sorted.map(s => s.id);

    fillLazyGrid(list, sorted, (entry) => {
      const tile = document.createElement('div');
      tile.className = 'card-tile';
      tile.dataset.cardId = entry.id;
      tile.dataset.rarity = entry.def.rarity;
      tile.addEventListener('click', () => openCardDetail(entry.id, 'deck'));
      tile.draggable = true;
      tile.addEventListener('dragstart', (e) => {
        if (!e.dataTransfer) return;
        e.dataTransfer.setData(DRAG_MIME_REMOVE, entry.id);
        e.dataTransfer.effectAllowed = 'move';
        tile.classList.add('dragging');
        hideTooltip();
      });
      tile.addEventListener('dragend', () => tile.classList.remove('dragging'));
      return tile;
    }, (tile, entry) => {
      if (prefs.viewMode === 'row') {
        tile.classList.add('row');
        tile.innerHTML = renderRowMarkup(entry.def, `×${entry.count}`);
        return;
      }
      const { card, requests } = buildTileCardWithDeferredArt(entry.def);
      tile.appendChild(card);
      slotData.get(tile)?.requests.push(...requests);

      const count = document.createElement('div');
      count.className = 'tile-count';
      count.textContent = `×${entry.count}`;
      card.appendChild(count);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'tile-remove';
      remove.textContent = '−';
      remove.title = 'Remove one copy';
      remove.addEventListener('click', (e) => {
        e.stopPropagation();
        removeCardFromDeck(workingDeck, entry.id);
        renderCollection();
        renderDeck();
        updateCounter();
      });
      card.appendChild(remove);
    }, 'deck');
  }

  function updateCounter() {
    const config = getConfig();
    const totalCards = getDeckCardCount(workingDeck);
    const target = config.minDeckSize;
    const counter = el.querySelector('.deck-counter');
    if (counter) {
      counter.textContent = `${totalCards}/${target}`;
      counter.classList.toggle('complete', totalCards === target);
    }
    const saveBtn = el.querySelector('#db-save') as HTMLButtonElement;
    if (saveBtn) {
      saveBtn.disabled = totalCards !== target;
      if (totalCards > target) {
        saveBtn.title = `Remove ${totalCards - target} card${totalCards - target === 1 ? '' : 's'}`;
      } else if (totalCards < target) {
        saveBtn.title = `Add ${target - totalCards} card${target - totalCards === 1 ? '' : 's'}`;
      } else {
        saveBtn.title = '';
      }
    }
  }

  render();
}
